// Audio-Worklet des Gasts: Mikrofon → 32 kHz → ADPCM-Rahmen, und empfangene Rahmen →
// Jitter-Puffer → Kopfhörer. Läuft im Audio-Thread des Browsers, damit nichts stockt,
// wenn die Seite gerade zeichnet. Wird als Blob geladen (kein eigener Build-Schritt).

import { makeCodec } from "./adpcm";

const PROCESSOR = String.raw`
const FRAME = 640;
const LINK_RATE = 32000;
const TARGET = 4;      // 80 ms Reserve – Browser takten unruhiger als das Studio
const MAX_FRAMES = 20;

class LinkProcessor extends AudioWorkletProcessor {
  constructor() {
    super();
    this.enc = makeCodec();
    this.dec = makeCodec();
    // Senderichtung: Gerät → 32 kHz
    this.upStep = sampleRate / LINK_RATE;
    this.upPhase = 0;
    this.upLast = 0;
    this.pending = new Float32Array(FRAME);
    this.pendingLen = 0;
    this.muted = false;
    // Empfang: 32 kHz → Gerät, mit Nachregeln
    this.frames = new Map();
    this.next = -1;
    this.playing = false;
    this.last = new Float32Array(FRAME);
    this.conceal = 0;
    this.stage = new Float32Array(0);
    this.stagePos = 0;
    this.downPhase = 0;
    this.downLast = 0;
    this.gain = 1;
    this.history = [];
    this.micPeak = 0;
    this.retPeak = 0;
    this.ticks = 0;
    this.port.onmessage = (e) => {
      const m = e.data;
      if (m.type === "frame") this.push(m.seq, new Uint8Array(m.payload));
      else if (m.type === "mute") this.muted = !!m.on;
      else if (m.type === "gain") this.gain = m.value;
    };
  }

  record(ok) {
    this.history.push(ok);
    if (this.history.length > 500) this.history.shift();
  }

  push(seq, payload) {
    const samples = this.dec.decode(payload);
    if (!samples) return;
    if (this.next >= 0 && seq + 50 < this.next) {
      this.frames.clear();
      this.next = -1;
      this.playing = false;
    } else if (this.playing && seq < this.next) {
      this.record(false);
      return;
    }
    this.frames.set(seq, samples);
    while (this.frames.size > MAX_FRAMES) {
      const oldest = Math.min(...this.frames.keys());
      this.frames.delete(oldest);
      if (this.next <= oldest) this.next = oldest + 1;
    }
  }

  pop() {
    if (!this.playing) {
      if (this.frames.size < TARGET) return null;
      this.playing = true;
      this.next = Math.min(...this.frames.keys());
    }
    const f = this.frames.get(this.next);
    if (f) {
      this.frames.delete(this.next);
      this.next += 1;
      this.conceal = 0;
      this.last = f;
      this.record(true);
      return f;
    }
    this.record(false);
    if (this.frames.size === 0) this.playing = false;
    else this.next += 1;
    this.conceal += 1;
    const g = [0, 0.6, 0.3, 0.1][this.conceal] || 0;
    this.last = this.last.map((v) => v * g);
    return this.last;
  }

  process(inputs, outputs) {
    // ---- Mikrofon → Rahmen ----
    const input = inputs[0] && inputs[0][0];
    if (input) {
      for (let i = 0; i < input.length; i++) {
        const v = this.muted ? 0 : input[i];
        this.micPeak = Math.max(this.micPeak, Math.abs(v));
        // Linear umrechnen: Ausgabepunkte liegen im Abstand upStep auf der Eingangsachse.
        while (this.upPhase < 1) {
          this.pending[this.pendingLen++] = this.upLast + (v - this.upLast) * this.upPhase;
          this.upPhase += this.upStep;
          if (this.pendingLen === FRAME) {
            const payload = this.enc.encode(this.pending);
            this.port.postMessage({ type: "enc", payload: payload.buffer }, [payload.buffer]);
            this.pendingLen = 0;
          }
        }
        this.upPhase -= 1;
        this.upLast = v;
      }
    }

    // ---- Rahmen → Kopfhörer ----
    const out = outputs[0][0];
    const depth = this.frames.size;
    const speed = depth > TARGET + 3 ? 1.005 : depth > TARGET + 1 ? 1.002 : depth + 1 < TARGET ? 0.998 : 1;
    const step = (LINK_RATE / sampleRate) * speed;
    for (let i = 0; i < out.length; i++) {
      if (this.stagePos >= this.stage.length) {
        const f = this.pop();
        if (!f) {
          out[i] = 0;
          continue;
        }
        // 32 kHz-Rahmen in Geräte-Rate umrechnen
        const resampled = [];
        while (this.downPhase < f.length) {
          const idx = Math.floor(this.downPhase);
          const frac = this.downPhase - idx;
          const a = idx === 0 ? this.downLast : f[idx - 1];
          resampled.push(a + (f[idx] - a) * frac);
          this.downPhase += step;
        }
        this.downPhase -= f.length;
        this.downLast = f[f.length - 1];
        this.stage = Float32Array.from(resampled);
        this.stagePos = 0;
        if (this.stage.length === 0) {
          out[i] = 0;
          continue;
        }
      }
      const v = this.stage[this.stagePos++] * this.gain;
      out[i] = v;
      this.retPeak = Math.max(this.retPeak, Math.abs(v));
    }
    for (let c = 1; c < outputs[0].length; c++) outputs[0][c].set(out);

    if (++this.ticks % 20 === 0) {
      const missed = this.history.filter((ok) => !ok).length;
      this.port.postMessage({ type: "meter", mic: this.micPeak, ret: this.retPeak, bufferMs: depth * 20, loss: this.history.length ? missed / this.history.length : 0 });
      this.micPeak *= 0.5;
      this.retPeak *= 0.5;
    }
    return true;
  }
}

registerProcessor("wr-link", LinkProcessor);
`;

/** Quelltext für `audioWorklet.addModule` (als Blob-URL). */
export function workletSource(): string {
  return `const makeCodec = ${makeCodec.toString()};\n${PROCESSOR}`;
}
